/**
 * Info-Leiste im Planet-Hub zwischen Titel und Tabs.
 * Flaches 4-Spalten-Grid — keine Zell-Rahmen, nur Typografie + Farbe.
 */
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import type { BuildingType } from '../config/BuildingTypes';
import type { EnergyBalanceInfo } from '../systems/EnergyBalance';
import { BUILDING_DEFS } from '../config/BuildingsConfig';
import { factoryProduction, storageCapacity } from '../systems/BuildingFormulas';
import { fmtSpan, formatNumber } from '../math/formatNumber';
import { isMobile } from './UITheme';

const BAR_DESKTOP =
  'display:grid;grid-template-columns:repeat(auto-fit, minmax(clamp(140px,22vw,220px), 1fr));' +
  'gap:clamp(8px, 2vw, 24px);' +
  'padding:var(--ui-pad-sm) var(--ui-pad-xl) var(--ui-pad-xs);font-family:monospace;' +
  'font-size:var(--ui-font-xs);flex-shrink:0;' +
  'border-bottom:1px solid rgba(136,204,255,0.06);';

const BAR_MOBILE =
  'display:grid;grid-template-columns:repeat(4,1fr);gap:6px;' +
  'padding:8px 12px;font-family:monospace;' +
  'font-size:13px;flex-shrink:0;' +
  'border-bottom:1px solid rgba(136,204,255,0.06);';

const LBL = 'font-size:var(--ui-font-xs);letter-spacing:1.5px;color:#4a6a7a;margin-bottom:4px;';
const ROW = 'display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;';
const TAG = 'color:#3a5a6a;font-size:var(--ui-font-xs);';

const RES: { factory: BuildingType; storage: BuildingType;
  key: 'feronium' | 'chrysalium' | 'neurogel';
  deposit: 'feroniumCap' | 'chrysaliumCap' | 'neurogelCap';
  icon: string; label: string; color: string }[] = [
  { factory: 'feronium_mine', storage: 'feronium_storage', key: 'feronium',
    deposit: 'feroniumCap', icon: '\u26CF', label: 'FERONIUM', color: '#6af' },
  { factory: 'chrysalium_burner', storage: 'chrysalium_storage', key: 'chrysalium',
    deposit: 'chrysaliumCap', icon: '\uD83D\uDD25', label: 'CHRYSALIUM', color: '#fa6' },
  { factory: 'neurogel_extractor', storage: 'neurogel_tank', key: 'neurogel',
    deposit: 'neurogelCap', icon: '\uD83E\uDDEC', label: 'NEUROGEL', color: '#6fa' },
];

export function createHubHeader(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText = isMobile() ? BAR_MOBILE : BAR_DESKTOP;
  return el;
}

export function updateHubHeader(
  el: HTMLDivElement, planet: PlanetInfo,
  levels: Map<BuildingType, number>,
  energy: EnergyBalanceInfo,
  known: boolean,
): void {
  if (!known) { el.innerHTML = ''; return; }

  if (isMobile()) {
    updateHubHeaderCompact(el, planet, levels, energy);
    return;
  }

  const cols: string[] = [];

  for (const r of RES) {
    const stored = Math.round(planet[r.key]);
    const dep = planet[r.deposit];
    const lv = levels.get(r.factory) ?? 0;
    const sLv = levels.get(r.storage) ?? 0;
    const sCap = Math.round(storageCapacity(sLv, BUILDING_DEFS[r.storage].baseRate));
    const prod = factoryProduction(lv, BUILDING_DEFS[r.factory].baseRate) * energy.modifier;
    cols.push(
      `<div>` +
      `<div style="${LBL}">${r.icon} ${r.label}</div>` +
      `<div style="${ROW}">` +
      `<span style="${TAG}">Lager</span>` +
      fmtSpan(stored, `color:${r.color};font-size:14px`, 't') +
      `<span style="color:#3a4a5a">/ </span>` +
      fmtSpan(sCap, 'color:#3a4a5a', 't') +
      `</div>` +
      `<div style="${ROW}margin-top:2px;">` +
      `<span style="${TAG}">Vorkommen</span>` +
      fmtSpan(dep, 'color:#556', 't') +
      `<span style="${TAG};margin-left:6px">Prod.</span>` +
      `<span style="color:#556">+${prod.toFixed(1)}/s</span>` +
      `</div>` +
      `</div>`,
    );
  }

  const balColor = energy.balance >= 0 ? '#0f0' : '#f44';
  const modPct = Math.round(energy.modifier * 100);
  const modColor = modPct >= 100 ? '#0f0' : modPct > 50 ? '#ff0' : '#f44';

  cols.push(
    `<div>` +
    `<div style="${LBL}">\u26A1 ENERGIE</div>` +
    `<div style="${ROW}">` +
    `<span style="${TAG}">Erzeugung</span>` +
    fmtSpan(Math.round(energy.supply), 'color:#0f0') +
    `<span style="${TAG}">Bedarf</span>` +
    fmtSpan(Math.round(energy.demand), 'color:#f44') +
    `</div>` +
    `<div style="${ROW}margin-top:2px;">` +
    `<span style="${TAG}">Bilanz</span>` +
    `<span style="color:${balColor}">${energy.balance >= 0 ? '+' : ''}${Math.round(energy.balance)}</span>` +
    `<span style="${TAG};margin-left:6px">Effizienz</span>` +
    `<span style="color:${modColor}">${modPct}%</span>` +
    `</div>` +
    `</div>`,
  );

  el.innerHTML = cols.join('');
}

/** Compact single-line resource bar for mobile landscape */
function updateHubHeaderCompact(
  el: HTMLDivElement, planet: PlanetInfo,
  levels: Map<BuildingType, number>,
  energy: EnergyBalanceInfo,
): void {
  const CELL = 'display:flex;flex-direction:column;align-items:center;gap:1px;';
  const cells: string[] = [];

  for (const r of RES) {
    const stored = Math.round(planet[r.key]);
    const lv = levels.get(r.factory) ?? 0;
    const sLv = levels.get(r.storage) ?? 0;
    const sCap = Math.round(storageCapacity(sLv, BUILDING_DEFS[r.storage].baseRate));
    const prod = factoryProduction(lv, BUILDING_DEFS[r.factory].baseRate) * energy.modifier;
    cells.push(
      `<div style="${CELL}">` +
      `<div style="display:flex;align-items:baseline;gap:3px;">` +
      `<span style="color:${r.color}">${r.icon}</span>` +
      fmtSpan(stored, `color:${r.color};font-weight:bold;font-size:13px`) +
      `</div>` +
      `<div style="font-size:10px;color:#3a4a5a;white-space:nowrap;">` +
      `/ ${formatNumber(sCap)} <span style="color:#556">+${formatNumber(prod)}/s</span></div>` +
      `</div>`,
    );
  }

  const modPct = Math.round(energy.modifier * 100);
  const modColor = modPct >= 100 ? '#0f0' : modPct > 50 ? '#ff0' : '#f44';
  const balColor = energy.balance >= 0 ? '#0f0' : '#f44';
  const balSign = energy.balance >= 0 ? '+' : '';
  cells.push(
    `<div style="${CELL}">` +
    `<div style="display:flex;align-items:baseline;gap:3px;">` +
    `<span style="color:#ff0">\u26A1</span>` +
    `<span style="color:${balColor};font-weight:bold;font-size:13px">${balSign}${Math.round(energy.balance)}</span>` +
    `</div>` +
    `<div style="font-size:10px;color:${modColor};">${modPct}%</div>` +
    `</div>`,
  );

  el.innerHTML = cells.join('');
}
